/**
 * <keystone-field-relationship> \u2014 foreign-key picker.
 *
 * Maps onto JSON Schema `type: "string"` + custom `x-references: <type>`.
 * Renders a <select> populated from `GET /api/entities/<target>`.
 *
 * Attributes
 * ----------
 *   target            Entity type to fetch options from (e.g. "supplier").
 *                     Required; nothing renders without it.
 *
 *   display-field     Which field of the target entity to show in the
 *                     option label. Default "name". The schema-driven
 *                     `<keystone-form>` passes the value from
 *                     `x-display-field` when present.
 *
 *   options-url       Override for the fetch URL. Default is
 *                     `/api/entities/<target>?fields=id,<display-field>`.
 *                     Useful for tests and for the AI configurator
 *                     emitting bespoke search URLs later.
 *
 * Loading model
 * -------------
 *   On connect, fetch the option list. Until it resolves, the <select>
 *   shows a single disabled "Loading\u2026" placeholder so the widget is
 *   visible but not interactive. On error, the placeholder text becomes
 *   "(failed to load)" and we surface a field-level error.
 *
 * Out of scope for v2
 * -------------------
 *   - Server-side search (typeahead). When the list endpoint grows a
 *     `?q=...` param, switch the picker over.
 *   - Lazy / chunked load. Today we fetch up to the server's default
 *     limit (50, capped at 100) on first render.
 */

import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { fieldStyles, humaniseFieldName } from "./base.js";

interface Option {
  id: string;
  label: string;
}

type Status = "idle" | "loading" | "ready" | "error";

@customElement("keystone-field-relationship")
export class KeystoneFieldRelationship extends LitElement {
  static override styles = fieldStyles;

  @property({ type: String }) name = "";
  @property({ type: String }) label = "";
  @property({ type: String }) value = "";
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error: string | null = null;
  @property({ type: Boolean }) disabled = false;

  @property({ type: String }) target = "";
  @property({ attribute: "display-field", type: String }) displayField = "name";
  @property({ attribute: "options-url", type: String }) optionsUrl = "";

  @state() private status: Status = "idle";
  @state() private options: Option[] = [];

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.target) {
      void this.loadOptions();
    }
  }

  override willUpdate(changed: Map<string, unknown>): void {
    // Re-fetch if the target or URL changes after first render.
    if (
      (changed.has("target") || changed.has("optionsUrl")) &&
      this.target &&
      this.status !== "loading"
    ) {
      void this.loadOptions();
    }
  }

  override updated(): void {
    if (this.error) this.setAttribute("data-error", "");
    else this.removeAttribute("data-error");
  }

  private resolveUrl(): string {
    if (this.optionsUrl) return this.optionsUrl;
    const params = new URLSearchParams({ fields: `id,${this.displayField}` });
    return `/api/entities/${encodeURIComponent(this.target)}?${params}`;
  }

  private async loadOptions(): Promise<void> {
    this.status = "loading";
    try {
      const res = await fetch(this.resolveUrl(), {
        headers: { accept: "application/json" },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const body = (await res.json()) as {
        entities: Array<Record<string, unknown>>;
      };
      this.options = body.entities.map((entity) => ({
        id: String(entity.id ?? ""),
        label:
          (entity[this.displayField] as string | undefined) ??
          String(entity.id ?? "?"),
      }));
      this.status = "ready";
    } catch (_err) {
      this.status = "error";
      this.options = [];
    }
  }

  private onChange(e: Event): void {
    const select = e.target as HTMLSelectElement;
    this.value = select.value;
    this.dispatchEvent(
      new CustomEvent("keystone-field-input", {
        detail: { name: this.name, value: select.value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const label = this.label || humaniseFieldName(this.name);
    const inputId = `input-${this.name}`;

    const placeholderText =
      this.status === "loading"
        ? "Loading\u2026"
        : this.status === "error"
          ? "(failed to load options)"
          : this.required && !this.value
            ? `Select ${this.target}\u2026`
            : "\u2014 none \u2014";

    const placeholderDisabled =
      this.status !== "ready" || (this.required && !this.value);

    return html`
      <label for=${inputId}>
        ${label}${this.required
          ? html`<span class="required-marker" aria-hidden="true">*</span>`
          : ""}
      </label>
      <select
        id=${inputId}
        name=${this.name}
        ?required=${this.required}
        ?disabled=${this.disabled || this.status !== "ready"}
        aria-invalid=${this.error ? "true" : "false"}
        aria-describedby=${this.error ? `${inputId}-error` : ""}
        @change=${this.onChange}
      >
        <option
          value=""
          ?disabled=${placeholderDisabled}
          ?selected=${this.value === ""}
        >
          ${placeholderText}
        </option>
        ${this.options.map(
          (opt) => html`
            <option value=${opt.id} ?selected=${opt.id === this.value}>
              ${opt.label}
            </option>
          `,
        )}
      </select>
      ${this.error
        ? html`<p class="error" id="${inputId}-error" role="alert">
            ${this.error}
          </p>`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keystone-field-relationship": KeystoneFieldRelationship;
  }
}
